import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SlangCard from "@/components/SlangCard";
import { Search, Sparkles, X, PlayCircle } from "lucide-react";

export default function Home() {
  const [items, setItems] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [inputValue, setInputValue] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  // Debounce: push input into q after 300ms idle
  useEffect(() => {
    const t = setTimeout(() => setQ(inputValue.trim()), 300);
    return () => clearTimeout(t);
  }, [inputValue]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const [list, trend, cats] = await Promise.all([
        api.get("/slang", { params: { q: q || undefined, category: category !== "All" ? category : undefined } }),
        q || category !== "All" ? Promise.resolve({ data: { items: [] } }) : api.get("/slang/trending"),
        api.get("/slang/categories"),
      ]);
      if (!mounted) return;
      setItems(list.data.items);
      if (!q && category === "All") setTrending(trend.data.items);
      setCategories(["All", ...cats.data.categories]);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [q, category]);

  const runSearch = () => setQ(inputValue.trim());
  const clearSearch = () => { setInputValue(""); setQ(""); };

  const marqueeWords = useMemo(() => {
    const pool = (items.length ? items : []).slice(0, 30).map((s) => s.word);
    return pool.length ? [...pool, ...pool] : ["Rizz", "No Cap", "Bussin", "Slay", "Mid", "Delulu", "Touch Grass", "It's Giving"];
  }, [items]);

  const isFiltering = q || category !== "All";

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-paper dot-grid border-b-[3px] border-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="font-mono-tag text-xs uppercase mb-4 inline-block bg-lime brutal-sm px-2 py-1">
              <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" strokeWidth={3} /> The Gen Z decoder
            </p>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter">
              Speak <span className="text-hot">fluent</span><br />
              <span className="bg-lime px-2 inline-block rotate-1n">internet</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg max-w-xl">
              A living, vibing dictionary of Gen Z slang. Look up <b>Rizz</b>, <b>Clock it</b>, <b>8</b>, and 50+ more.
              Hear it out loud. Submit new ones. Our think tank vets every drop.
            </p>
            <Link
              data-testid="hero-walkthrough-cta"
              to="/walkthrough"
              className="mt-6 inline-flex items-center gap-2 bg-hot text-white font-display uppercase text-sm px-4 py-2 brutal-sm brutal-hover brutal-active"
            >
              <PlayCircle className="h-4 w-4" strokeWidth={3} /> Watch 1-min walkthrough
            </Link>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="bg-lilac brutal p-6 rotate-2">
              <div className="font-mono-tag text-[10px] uppercase mb-2">word of the moment</div>
              <div className="font-display text-5xl leading-none">{(trending[0]?.word) || "Rizz"}</div>
              <div className="font-hand text-2xl mt-2 leading-tight">"that gives unmatched rizz"</div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-t-[3px] border-b-[3px] border-black bg-hot text-white overflow-hidden">
          <div className="marquee-track py-3">
            {marqueeWords.map((w, i) => (
              <span key={i} className="font-display text-2xl px-6">{w} ✦</span>
            ))}
          </div>
        </div>
      </section>

      {/* Search + filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" strokeWidth={3} />
              <input
                data-testid="search-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
                placeholder="Search rizz, slay, mid, delulu..."
                className="w-full pl-10 pr-10 py-3 brutal-sm bg-white font-display text-base focus:outline-none"
              />
              {inputValue && (
                <button data-testid="search-clear-btn" type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2" aria-label="Clear search">
                  <X className="h-4 w-4" strokeWidth={3} />
                </button>
              )}
            </div>
            <button
              data-testid="search-submit-btn"
              type="button"
              onClick={runSearch}
              className="px-4 py-3 font-display uppercase text-sm bg-hot text-white brutal-sm brutal-hover brutal-active"
            >
              Search
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-filter-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`px-3 py-2 font-mono-tag text-xs uppercase brutal-sm brutal-hover ${category === c ? "bg-hot text-white" : "bg-white"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Trending row */}
        {!isFiltering && trending.length > 0 && (
          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">Trending<span className="text-hot">.</span></h2>
              <span className="font-mono-tag text-xs uppercase">this week</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {trending.slice(0, 4).map((s, i) => (
                <SlangCard key={s.id} slang={s} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* All slang */}
        <div className="mt-14">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">
              {isFiltering ? "Results" : "All the words"}
              <span className="text-hot">.</span>
            </h2>
            <span data-testid="results-count" className="font-mono-tag text-xs uppercase">{items.length} entries</span>
          </div>

          {loading ? (
            <div className="font-mono-tag text-sm uppercase">loading...</div>
          ) : items.length === 0 ? (
            <div data-testid="empty-state" className="brutal-sm bg-white p-8 text-center font-display text-xl">
              No slang found. Try a different query.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map((s, i) => (
                <SlangCard key={s.id} slang={s} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="border-t-[3px] border-black bg-altsoft py-8 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="font-display text-lg">made with vibes ✦ slang.dex</p>
          <p className="font-mono-tag text-xs uppercase">© 2026 — words live forever on the internet</p>
        </div>
      </footer>
    </div>
  );
}
